import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Plus, Pencil, Trash2, Mail, CheckCircle2, RotateCcw, Star } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { apiClient, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

const CATEGORIES = ["MSME", "Women", "SC_ST", "Youth", "Agri", "General"];

const emptyScheme = {
    name_en: "",
    name_ta: "",
    short_desc_en: "",
    short_desc_ta: "",
    description_en: "",
    description_ta: "",
    category: "MSME",
    subsidy: "",
    eligibility_en: "",
    eligibility_ta: "",
    benefits_en: "",
    benefits_ta: "",
    application_process_en: "",
    application_process_ta: "",
    pdf_url: "",
    is_featured: false,
};

export default function AdminDashboard() {
    const { user } = useAuth();
    const { t } = useLang();
    const [schemes, setSchemes] = useState([]);
    const [enquiries, setEnquiries] = useState([]);
    const [editing, setEditing] = useState(null); // scheme object being edited
    const [dialogOpen, setDialogOpen] = useState(false);

    const loadSchemes = () =>
        apiClient.get("/schemes").then((r) => setSchemes(r.data)).catch(() => {});
    const loadEnquiries = () =>
        apiClient
            .get("/enquiries")
            .then((r) => setEnquiries(r.data))
            .catch(() => setEnquiries([]));

    useEffect(() => {
        if (user && user.role === "admin") {
            loadSchemes();
            loadEnquiries();
        }
    }, [user]);

    if (user === null) {
        return <div className="p-16 text-center text-gray-500">Checking session…</div>;
    }
    if (!user || user.role !== "admin") {
        return <Navigate to="/admin/login" replace />;
    }

    const openNew = () => {
        setEditing({ ...emptyScheme });
        setDialogOpen(true);
    };
    const openEdit = (s) => {
        setEditing({ ...s });
        setDialogOpen(true);
    };

    const saveScheme = async () => {
        if (!editing) return;
        try {
            const payload = { ...editing };
            delete payload.id;
            delete payload.created_at;
            delete payload.updated_at;
            if (editing.id) {
                await apiClient.put(`/schemes/${editing.id}`, payload);
                toast.success("Scheme updated");
            } else {
                await apiClient.post("/schemes", payload);
                toast.success("Scheme created");
            }
            setDialogOpen(false);
            setEditing(null);
            loadSchemes();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
        }
    };

    const deleteScheme = async (id) => {
        if (!window.confirm(t.admin.confirmDelete)) return;
        try {
            await apiClient.delete(`/schemes/${id}`);
            toast.success("Deleted");
            loadSchemes();
        } catch (e) {
            toast.error("Delete failed");
        }
    };

    const updateEnquiryStatus = async (id, status) => {
        try {
            await apiClient.put(`/enquiries/${id}`, null, { params: { status } });
            loadEnquiries();
        } catch (e) {
            toast.error("Update failed");
        }
    };

    const deleteEnquiry = async (id) => {
        if (!window.confirm("Delete this enquiry?")) return;
        try {
            await apiClient.delete(`/enquiries/${id}`);
            loadEnquiries();
        } catch (e) {
            toast.error("Delete failed");
        }
    };

    return (
        <div className="bg-[#F7FAFC] min-h-screen py-12" data-testid="admin-dashboard">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <div className="mb-8">
                    <div className="small-caps text-[#DD6B20] mb-2">
                        {t.admin.welcome}, {user.name}
                    </div>
                    <h1 className="font-serif text-4xl font-semibold text-[#1A365D]">
                        {t.admin.dashTitle}
                    </h1>
                </div>

                <Tabs defaultValue="schemes" className="w-full">
                    <TabsList className="mb-6 bg-white border border-gray-200">
                        <TabsTrigger value="schemes" data-testid="tab-schemes">
                            {t.admin.tabSchemes} ({schemes.length})
                        </TabsTrigger>
                        <TabsTrigger value="enquiries" data-testid="tab-enquiries">
                            {t.admin.tabEnquiries} ({enquiries.length})
                        </TabsTrigger>
                    </TabsList>

                    {/* Schemes tab */}
                    <TabsContent value="schemes">
                        <div className="flex justify-end mb-4">
                            <Button
                                onClick={openNew}
                                className="bg-[#DD6B20] hover:bg-[#C05621]"
                                data-testid="new-scheme-btn"
                            >
                                <Plus size={16} className="mr-2" /> {t.admin.newScheme}
                            </Button>
                        </div>

                        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead className="bg-[#F7FAFC]">
                                        <tr className="text-left">
                                            <Th>Name</Th>
                                            <Th>Category</Th>
                                            <Th>Subsidy</Th>
                                            <Th>Featured</Th>
                                            <Th className="text-right">Actions</Th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {schemes.map((s) => (
                                            <tr
                                                key={s.id}
                                                className="border-t border-gray-100 hover:bg-gray-50"
                                                data-testid={`admin-scheme-row-${s.id}`}
                                            >
                                                <Td className="max-w-md">
                                                    <div className="font-medium text-[#1A365D]">
                                                        {s.name_en}
                                                    </div>
                                                    <div className="text-xs text-gray-500">{s.name_ta}</div>
                                                </Td>
                                                <Td>
                                                    <Badge variant="outline">{s.category}</Badge>
                                                </Td>
                                                <Td className="text-gray-700">{s.subsidy}</Td>
                                                <Td>
                                                    {s.is_featured ? (
                                                        <Star size={14} className="text-[#DD6B20] fill-[#DD6B20]" />
                                                    ) : (
                                                        <span className="text-gray-400">—</span>
                                                    )}
                                                </Td>
                                                <Td className="text-right whitespace-nowrap">
                                                    <button
                                                        onClick={() => openEdit(s)}
                                                        className="text-[#1A365D] hover:text-[#DD6B20] p-2"
                                                        data-testid={`edit-scheme-${s.id}`}
                                                    >
                                                        <Pencil size={14} />
                                                    </button>
                                                    <button
                                                        onClick={() => deleteScheme(s.id)}
                                                        className="text-[#C53030] hover:text-red-700 p-2"
                                                        data-testid={`delete-scheme-${s.id}`}
                                                    >
                                                        <Trash2 size={14} />
                                                    </button>
                                                </Td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </TabsContent>

                    {/* Enquiries tab */}
                    <TabsContent value="enquiries">
                        {enquiries.length === 0 ? (
                            <div
                                className="text-center py-16 text-gray-500 bg-white rounded-lg border border-dashed border-gray-300"
                                data-testid="admin-no-enquiries"
                            >
                                <Mail className="mx-auto mb-3 text-gray-300" size={32} />
                                {t.admin.noEnquiries}
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {enquiries.map((e) => (
                                    <div
                                        key={e.id}
                                        className="bg-white p-6 rounded-lg border border-gray-200"
                                        data-testid={`enquiry-row-${e.id}`}
                                    >
                                        <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                                            <div>
                                                <div className="font-serif text-lg font-semibold text-[#1A365D]">
                                                    {e.subject}
                                                </div>
                                                <div className="text-xs text-gray-500 mt-1">
                                                    {e.name} · {e.email} · {e.phone} · {e.district}
                                                </div>
                                                <div className="text-[10px] text-gray-400 uppercase tracking-widest mt-1">
                                                    {new Date(e.created_at).toLocaleString()}
                                                </div>
                                            </div>
                                            <Badge
                                                className={
                                                    e.status === "new"
                                                        ? "bg-[#DD6B20] hover:bg-[#DD6B20]"
                                                        : "bg-[#2F855A] hover:bg-[#2F855A]"
                                                }
                                            >
                                                {e.status === "new"
                                                    ? t.admin.statusNew
                                                    : t.admin.statusHandled}
                                            </Badge>
                                        </div>
                                        <p className="text-sm text-gray-700 whitespace-pre-line mb-4">
                                            {e.message}
                                        </p>
                                        <div className="flex gap-2">
                                            {e.status === "new" ? (
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => updateEnquiryStatus(e.id, "handled")}
                                                    data-testid={`mark-handled-${e.id}`}
                                                >
                                                    <CheckCircle2 size={14} className="mr-2" />
                                                    {t.admin.markHandled}
                                                </Button>
                                            ) : (
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => updateEnquiryStatus(e.id, "new")}
                                                    data-testid={`mark-new-${e.id}`}
                                                >
                                                    <RotateCcw size={14} className="mr-2" />
                                                    {t.admin.markNew}
                                                </Button>
                                            )}
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => deleteEnquiry(e.id)}
                                                className="text-[#C53030] hover:text-red-700 hover:bg-red-50"
                                                data-testid={`delete-enquiry-${e.id}`}
                                            >
                                                <Trash2 size={14} className="mr-2" />
                                                {t.admin.deleteEnquiry}
                                            </Button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </TabsContent>
                </Tabs>

                {/* Scheme dialog */}
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle className="font-serif text-2xl text-[#1A365D]">
                                {editing?.id ? t.admin.editScheme : t.admin.newScheme}
                            </DialogTitle>
                        </DialogHeader>

                        {editing && (
                            <div className="space-y-4 py-2">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <FormField label="Name (EN)">
                                        <Input
                                            value={editing.name_en}
                                            onChange={(e) =>
                                                setEditing({ ...editing, name_en: e.target.value })
                                            }
                                            data-testid="form-name-en"
                                        />
                                    </FormField>
                                    <FormField label="Name (TA)">
                                        <Input
                                            value={editing.name_ta}
                                            onChange={(e) =>
                                                setEditing({ ...editing, name_ta: e.target.value })
                                            }
                                            data-testid="form-name-ta"
                                        />
                                    </FormField>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <FormField label="Category">
                                        <select
                                            value={editing.category}
                                            onChange={(e) =>
                                                setEditing({ ...editing, category: e.target.value })
                                            }
                                            className="w-full h-10 px-3 rounded-md border border-gray-300 bg-white text-sm"
                                            data-testid="form-category"
                                        >
                                            {CATEGORIES.map((c) => (
                                                <option key={c} value={c}>
                                                    {c}
                                                </option>
                                            ))}
                                        </select>
                                    </FormField>
                                    <FormField label="Subsidy">
                                        <Input
                                            value={editing.subsidy}
                                            onChange={(e) =>
                                                setEditing({ ...editing, subsidy: e.target.value })
                                            }
                                            data-testid="form-subsidy"
                                        />
                                    </FormField>
                                </div>

                                <FormField label="Short Description (EN)">
                                    <Textarea
                                        rows={2}
                                        value={editing.short_desc_en}
                                        onChange={(e) =>
                                            setEditing({ ...editing, short_desc_en: e.target.value })
                                        }
                                        data-testid="form-short-desc-en"
                                    />
                                </FormField>
                                <FormField label="Short Description (TA)">
                                    <Textarea
                                        rows={2}
                                        value={editing.short_desc_ta}
                                        onChange={(e) =>
                                            setEditing({ ...editing, short_desc_ta: e.target.value })
                                        }
                                        data-testid="form-short-desc-ta"
                                    />
                                </FormField>

                                <FormField label="Description (EN)">
                                    <Textarea
                                        rows={3}
                                        value={editing.description_en}
                                        onChange={(e) =>
                                            setEditing({ ...editing, description_en: e.target.value })
                                        }
                                        data-testid="form-description-en"
                                    />
                                </FormField>
                                <FormField label="Description (TA)">
                                    <Textarea
                                        rows={3}
                                        value={editing.description_ta}
                                        onChange={(e) =>
                                            setEditing({ ...editing, description_ta: e.target.value })
                                        }
                                        data-testid="form-description-ta"
                                    />
                                </FormField>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <FormField label="Eligibility (EN)">
                                        <Textarea
                                            rows={2}
                                            value={editing.eligibility_en}
                                            onChange={(e) =>
                                                setEditing({ ...editing, eligibility_en: e.target.value })
                                            }
                                        />
                                    </FormField>
                                    <FormField label="Eligibility (TA)">
                                        <Textarea
                                            rows={2}
                                            value={editing.eligibility_ta}
                                            onChange={(e) =>
                                                setEditing({ ...editing, eligibility_ta: e.target.value })
                                            }
                                        />
                                    </FormField>
                                    <FormField label="Benefits (EN)">
                                        <Textarea
                                            rows={2}
                                            value={editing.benefits_en}
                                            onChange={(e) =>
                                                setEditing({ ...editing, benefits_en: e.target.value })
                                            }
                                        />
                                    </FormField>
                                    <FormField label="Benefits (TA)">
                                        <Textarea
                                            rows={2}
                                            value={editing.benefits_ta}
                                            onChange={(e) =>
                                                setEditing({ ...editing, benefits_ta: e.target.value })
                                            }
                                        />
                                    </FormField>
                                    <FormField label="Application Process (EN)">
                                        <Textarea
                                            rows={2}
                                            value={editing.application_process_en}
                                            onChange={(e) =>
                                                setEditing({
                                                    ...editing,
                                                    application_process_en: e.target.value,
                                                })
                                            }
                                        />
                                    </FormField>
                                    <FormField label="Application Process (TA)">
                                        <Textarea
                                            rows={2}
                                            value={editing.application_process_ta}
                                            onChange={(e) =>
                                                setEditing({
                                                    ...editing,
                                                    application_process_ta: e.target.value,
                                                })
                                            }
                                        />
                                    </FormField>
                                </div>

                                <FormField label="PDF URL (optional)">
                                    <Input
                                        value={editing.pdf_url || ""}
                                        onChange={(e) =>
                                            setEditing({ ...editing, pdf_url: e.target.value })
                                        }
                                        data-testid="form-pdf-url"
                                    />
                                </FormField>

                                <div className="flex items-center gap-3 pt-2">
                                    <Switch
                                        checked={editing.is_featured}
                                        onCheckedChange={(v) =>
                                            setEditing({ ...editing, is_featured: v })
                                        }
                                        data-testid="form-featured"
                                    />
                                    <span className="text-sm text-gray-700">Show as featured on Home</span>
                                </div>
                            </div>
                        )}

                        <DialogFooter>
                            <Button
                                variant="outline"
                                onClick={() => setDialogOpen(false)}
                                data-testid="scheme-cancel-btn"
                            >
                                {t.admin.cancel}
                            </Button>
                            <Button
                                onClick={saveScheme}
                                className="bg-[#1A365D] hover:bg-[#2A4365]"
                                data-testid="scheme-save-btn"
                            >
                                {t.admin.save}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
        </div>
    );
}

function Th({ children, className = "" }) {
    return (
        <th className={`px-4 py-3 text-xs font-semibold uppercase tracking-widest text-gray-500 ${className}`}>
            {children}
        </th>
    );
}
function Td({ children, className = "" }) {
    return <td className={`px-4 py-3 align-top ${className}`}>{children}</td>;
}
function FormField({ label, children }) {
    return (
        <label className="block">
            <span className="text-xs uppercase tracking-widest font-semibold text-gray-600 mb-2 inline-block">
                {label}
            </span>
            {children}
        </label>
    );
}
